import { Skeleton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import {
  getGetJobQueryKey,
  getListJobsQueryKey,
  useGetCatalog,
} from "../../../api/generated/endpoints/generation/generation";
import type { Job } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { Describe } from "./Describe";
import { KindCards, type Kind } from "./KindCards";
import { SimpleRecent } from "./SimpleRecent";
import { SimpleResult } from "./SimpleResult";
import classes from "./simple.module.css";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/home2/simple";

export interface SimplePageProps {
  /**
   * An ad from a template: the templates and a chosen one's own form, which another area draws, so
   * the composition root hands them in (`app/pro-simple.tsx`). `onStarted` takes its run.
   */
  template: (onStarted: (job: Job) => void) => ReactNode;
}

/**
 * Simple (spec ui-perf/simple-studio): one guided page under Pro's header, with no nav. What to
 * make, for which product, then a prompt, on the catalog's defaults; the result with its next
 * steps, and the person's recent results below. Live has no such page.
 */
export function SimplePage({ template }: SimplePageProps) {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const queryClient = useQueryClient();
  // A template's run, shown as the page's result.
  const [run, setRun] = useState<string | null>(null);
  const kind = useSearch({ from: ROUTE, select: (search) => search.kind });
  const navigate = useNavigate({ from: "/teams/$teamId/brands/$brandId/home2/simple" });
  const catalog = useGetCatalog();
  const choose = (next: Kind) => void navigate({ search: { kind: next }, replace: true });
  const medium = kind === "image" || kind === "video" ? kind : undefined;
  return (
    <div className={classes.page}>
      <section className={classes.step} aria-labelledby="simple-kind">
        <h1 id="simple-kind" className={classes.title}>
          What do you want to make?
        </h1>
        <KindCards kind={kind} onKind={choose} />
      </section>
      {medium === undefined ? null : catalog.isError ? (
        <ErrorAlert error={catalog.error} />
      ) : catalog.data === undefined ? (
        <Skeleton className={classes.skeleton} />
      ) : (
        <Describe
          // A brand's or a kind's own draft: a switch starts it anew, as the Studio's composer.
          key={`${brandId} ${medium}`}
          teamId={teamId}
          brandId={brandId}
          catalog={catalog.data}
          medium={medium}
        />
      )}
      {kind === "ad" ? (
        <div className={classes.step}>
          {template((job) => {
            queryClient.setQueryData(getGetJobQueryKey(teamId, job.id), job);
            // As Pro's Templates page: the lists show the run at once.
            void queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) });
            setRun(job.id);
          })}
        </div>
      ) : null}
      {kind === "ad" && run !== null ? (
        <SimpleResult teamId={teamId} brandId={brandId} jobId={run} onAnother={() => setRun(null)} />
      ) : null}
      <SimpleRecent teamId={teamId} brandId={brandId} />
    </div>
  );
}
